import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useState, useEffect, useMemo, } from "react";
import { isValid, parse } from "date-fns";
import { useKeyboard } from "@react-aria/interactions";
import { KeyBoardKeys } from "../../../types";
import { useCalendar } from "../../use-calendar";
import { StyledSingleDatePicker, StyledDateInputWrapper, } from "./date-picker.styles";
import { DateInput } from "../date-input";
import { useOnClickOutside } from "../use-on-click-outside";
import { SingleCalendar } from "./single-calendar";
import { formatDate, getValidParsedDate, getWeekDayNames } from "../utils";
import { noop } from "../../use-calendar/helpers";
import { monthNames as defaultMonthNames, weekdayNamesShort as defaultWeekdayNamesShort, } from "../constants";
function Datepicker(props) {
    const [show, setShow] = useState(false);
    const { dateFormat = "dd.MM.yyyy", defaultDate, onDateSelected, selectedDates, monthNames = defaultMonthNames, weekdayNamesShort = defaultWeekdayNamesShort, weekStartsOn, appointments, dateError, label, onError, onDateChange, required, } = props;
    const [stateDate, setStateDate] = useState(new Date());
    const [val, setVal] = useState("");
    const datePickerRef = useRef(null);
    const weekdayNames = useMemo(() => {
        return getWeekDayNames(weekdayNamesShort, weekStartsOn);
    }, [weekStartsOn, weekdayNamesShort]);
    useEffect(() => {
        const parsedDefaultDate = defaultDate && getValidParsedDate(defaultDate, dateFormat);
        const selectedDate = parsedDefaultDate ||
            ((selectedDates === null || selectedDates === void 0 ? void 0 : selectedDates[0]) ? selectedDates[0] : undefined);
        if (selectedDate) {
            setVal(formatDate(selectedDate, dateFormat));
        }
        if (parsedDefaultDate) {
            if (typeof onDateSelected === "function")
                onDateSelected({ date: parsedDefaultDate });
            if (typeof onDateChange === "function")
                onDateChange(formatDate(parsedDefaultDate, dateFormat));
        }
    }, [dateFormat, defaultDate]);
    const hideDatePicker = () => {
        setShow(false);
    };
    const toggleDatePicker = () => {
        setShow((s) => !s);
    };
    const onKeyDown = (e) => {
        if (e.key === KeyBoardKeys.Escape)
            hideDatePicker();
    };
    useOnClickOutside(datePickerRef, hideDatePicker);
    const { keyboardProps } = useKeyboard({ onKeyDown });
    const calendarProps = useCalendar(Object.assign(Object.assign({}, props), { date: stateDate, show,
        selectedDates, onDateSelected: (dateObj) => {
            hideDatePicker();
            if (isValid(dateObj.date)) {
                if (typeof onDateSelected === "function")
                    onDateSelected(dateObj);
                if (typeof onDateChange === "function")
                    onDateChange(formatDate(dateObj.date, dateFormat));
                setVal(formatDate(dateObj.date, dateFormat));
                setStateDate(dateObj.date);
            }
        }, manageFocus: true, weekStartsOn }));
    const handleMonthYearOnChange = (newDate) => {
        if (isValid(newDate)) {
            setStateDate(newDate);
        }
    };
    return (_jsxs(StyledSingleDatePicker, Object.assign({}, keyboardProps, { ref: datePickerRef }, { children: [_jsx(StyledDateInputWrapper, { children: _jsx(DateInput, { onBlur: noop, onFocus: hideDatePicker, value: val, show: show, required: required, error: !show ? dateError : undefined, alwaysShowMask: true, onValueChange: ({ value }) => {
                        setVal(value);
                        if (typeof onDateChange === "function") {
                            onDateChange(value);
                        }
                        if (value.length === dateFormat.length) {
                            const parseDate = parse(value, dateFormat, new Date());
                            if (isValid(parseDate)) {
                                if (typeof onDateSelected === "function") {
                                    onDateSelected({
                                        date: parseDate,
                                    });
                                }
                                setStateDate(parseDate);
                            }
                            else if (typeof onError === "function") {
                                onError();
                            }
                        }
                    }, maskString: dateFormat, mask: dateFormat.replace(/[A-Z]/gi, "0"), onReset: () => {
                        setVal("");
                        hideDatePicker();
                        setStateDate(new Date());
                        if (typeof onDateChange === "function") {
                            onDateChange("");
                        }
                        if (typeof onDateSelected === "function") {
                            onDateSelected({
                                date: undefined,
                            });
                        }
                    }, withIcon: true, onIconClick: toggleDatePicker, label: label }) }), show && (_jsx(SingleCalendar, Object.assign({ appointments: appointments, selectedMonth: stateDate, onSelectYear: handleMonthYearOnChange, onSelectMonth: handleMonthYearOnChange, monthNames: monthNames, weekdayNamesShort: weekdayNames }, calendarProps)))] })));
}
export { Datepicker };
